import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Loader2 } from "lucide-react";
import { Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { useCurrency } from "@/hooks/useCurrency";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  name: "",
  description: "",
  delivery_charge: 10,
  min_order_value: 35,
  estimated_minutes: 30,
  sort_order: 0,
  status: "Active",
};

export default function DeliveryAreaMaster() {
  const { user } = useAuth();
  const { format, step: moneyStep, code: currencyCode } = useCurrency();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: areas = [], isLoading } = useQuery({
    queryKey: ["delivery-areas", branchId],
    queryFn: async () => {
      const { data } = await api.get("/delivery-areas", { params: { branch_id: branchId } });
      return data.delivery_areas;
    },
    enabled: Boolean(branchId),
  });

  const filtered = areas.filter(
    (a) =>
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.code.toLowerCase().includes(search.toLowerCase()),
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description || null,
        delivery_charge: Number(form.delivery_charge),
        min_order_value: Number(form.min_order_value),
        estimated_minutes: Number(form.estimated_minutes),
        sort_order: Number(form.sort_order),
        status: form.status,
      };
      if (editingId) {
        return api.put(`/delivery-areas/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/delivery-areas", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-areas", branchId] });
      toast.success(editingId ? "Delivery area updated" : "Delivery area created");
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: () => toast.error("Failed to save delivery area"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/delivery-areas/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-areas", branchId] });
      toast.success("Delivery area deleted");
    },
    onError: () => toast.error("Failed to delete delivery area"),
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY, code: `DA-${String(areas.length + 1).padStart(2, "0")}` });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      description: row.description || "",
      delivery_charge: row.delivery_charge,
      min_order_value: row.min_order_value,
      estimated_minutes: row.estimated_minutes,
      sort_order: row.sort_order,
      status: row.status,
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error("Code and area name are required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Delivery Area Master"
      subtitle={`${areas.length} zones — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="dam"
      formTitle={editingId ? form.name || "Edit Delivery Area" : "New Delivery Area"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="dam"
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <DataTable
              testIdPrefix="dam"
              onRowClick={openEdit}
              columns={[
                { key: "code", label: "Code", mono: true, bold: true },
                { key: "name", label: "Area Name", bold: true },
                { key: "delivery_charge", label: "Charge", align: "right", render: (v) => format(v) },
                { key: "min_order_value", label: "Min Order", align: "right", render: (v) => format(v) },
                { key: "estimated_minutes", label: "ETA (min)", align: "right" },
                { key: "status", label: "Status", render: (v) => <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill> },
              ]}
              rows={filtered}
              rowActions={(row) => [
                { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                {
                  title: "Delete",
                  icon: <Trash2 size={14} />,
                  danger: true,
                  onClick: () => {
                    if (window.confirm(`Delete area ${row.name}?`)) {
                      deleteMutation.mutate(row.id);
                    }
                  },
                },
              ]}
            />
          )}
        </>
      }
    >
      <div className="grid gap-3 max-w-md">
        <Field label="Code *">
          <Input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            className="h-9 font-mono"
            disabled={Boolean(editingId)}
            autoFocus={!editingId}
          />
        </Field>
        <Field label="Area Name *">
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            placeholder="e.g. Dubai Marina"
            autoFocus={Boolean(editingId)}
          />
        </Field>
        <Field label="Description">
          <Textarea value={form.description} onChange={(e) => setField("description", e.target.value)} className="min-h-[60px]" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Delivery Charge (${currencyCode})`}>
            <Input type="number" step={moneyStep} value={form.delivery_charge} onChange={(e) => setField("delivery_charge", e.target.value)} className="h-9 text-right font-mono" />
          </Field>
          <Field label={`Min Order (${currencyCode})`}>
            <Input type="number" step={moneyStep} value={form.min_order_value} onChange={(e) => setField("min_order_value", e.target.value)} className="h-9 text-right font-mono" />
          </Field>
          <Field label="ETA (minutes)">
            <Input type="number" value={form.estimated_minutes} onChange={(e) => setField("estimated_minutes", e.target.value)} className="h-9 text-right" />
          </Field>
          <Field label="Sort Order">
            <Input type="number" value={form.sort_order} onChange={(e) => setField("sort_order", e.target.value)} className="h-9 text-right" />
          </Field>
        </div>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => setField("status", v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </MasterCrudShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
